import { Component, type ReactNode } from "react";

const RELOAD_KEY = "app-chunk-reload-at";

function isChunkError(err: unknown): boolean {
  const msg = String((err as Error)?.message ?? err ?? "");
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError/i.test(msg);
}

/** Reload once (per 30s) when a lazy chunk is stale after a rebuild/deploy. */
function tryChunkReload(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < 30_000) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch { /* storage unavailable */ }
  window.location.reload();
  return true;
}

if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (e) => {
    e.preventDefault();
    tryChunkReload();
  });
  window.addEventListener("unhandledrejection", (e) => {
    if (isChunkError(e.reason)) tryChunkReload();
  });
}

export default class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error) {
    console.error("App crashed:", error);
    if (isChunkError(error)) tryChunkReload();
  }

  private errorPath = "";
  private timer?: number;

  componentDidUpdate() {
    if (this.state.error && !this.timer) {
      this.errorPath = window.location.pathname;
      // Clear the error once the user navigates elsewhere (e.g. sidebar link).
      this.timer = window.setInterval(() => {
        if (window.location.pathname !== this.errorPath) {
          window.clearInterval(this.timer);
          this.timer = undefined;
          this.setState({ error: null });
        }
      }, 300);
    }
  }

  componentWillUnmount() {
    if (this.timer) window.clearInterval(this.timer);
  }


  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-center text-foreground">
        <h1 className="text-xl font-semibold">Something went wrong</h1>
        <p className="max-w-md text-sm text-muted-foreground">This page hit an unexpected error. Reloading usually fixes it.</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground"
        >
          Reload page
        </button>
      </div>
    );
  }
}
